export interface BuildValidLocationSetInput {
  catalogDefaultLocationId?: string | null;
  catalogStorefrontLocationId?: string | null;
  latestReceiptLocationId?: string | null;
  inventoryLocationIds?: string[];
}

/**
 * Builds the set of valid location IDs for a line item.
 * Combines the catalog's warehouse location, storefront location, and any
 * existing inventory locations for that catalog item.
 * Returns null when the combined set is empty, signalling the "show all"
 * fallback (i.e., the item has no configured locations and no existing inventory).
 */
export const buildValidLocationSet = ({
  catalogDefaultLocationId,
  catalogStorefrontLocationId,
  latestReceiptLocationId,
  inventoryLocationIds,
}: BuildValidLocationSetInput): Set<string> | null => {
  const validIds = new Set<string>();
  if (catalogDefaultLocationId) validIds.add(catalogDefaultLocationId);
  if (catalogStorefrontLocationId) validIds.add(catalogStorefrontLocationId);
  if (latestReceiptLocationId) validIds.add(latestReceiptLocationId);
  for (const id of inventoryLocationIds ?? []) {
    validIds.add(id);
  }
  return validIds.size > 0 ? validIds : null;
};

export interface ReceiveOrderLineItemPayloadInput {
  id: string;
  outstandingQuantity: number;
}

export interface ReceiveOrderCartEntryPayloadInput {
  quantity: number;
  locationId?: string | null;
}

export interface CompleteReceiveOrderPayloadItem {
  lineItemId: string;
  quantity: number;
  locationId?: string | null;
}

export interface ResolveReceiveLocationIdInput {
  selectedLocationId?: string | null;
  cartLocationId?: string | null;
  latestReceiptLocationId?: string | null;
  catalogDefaultLocationId?: string | null;
  isValidLocationId: (locationId: string) => boolean;
}

export const buildReceiveAllCart = (
  items: ReceiveOrderLineItemPayloadInput[],
  resolveLocationId: (lineItemId: string) => string | null | undefined,
): Record<string, ReceiveOrderCartEntryPayloadInput> => {
  return Object.fromEntries(
    items
      .filter((item) => item.outstandingQuantity > 0)
      .map((item) => [
        item.id,
        {
          quantity: item.outstandingQuantity,
          locationId: resolveLocationId(item.id) ?? null,
        },
      ]),
  );
};

export const buildCompleteReceiptItems = (
  receivedCart: Record<string, ReceiveOrderCartEntryPayloadInput>,
): CompleteReceiveOrderPayloadItem[] => {
  return Object.entries(receivedCart)
    .map(([lineItemId, entry]) => ({
      lineItemId,
      quantity: entry.quantity,
      locationId: entry.locationId ?? undefined,
    }))
    .filter((item) => item.quantity > 0);
};

export const resolveReceiveLocationId = ({
  selectedLocationId,
  cartLocationId,
  latestReceiptLocationId,
  catalogDefaultLocationId,
  isValidLocationId,
}: ResolveReceiveLocationIdInput): string | null => {
  const candidates = [
    selectedLocationId,
    cartLocationId,
    latestReceiptLocationId,
    catalogDefaultLocationId,
  ];
  for (const candidate of candidates) {
    if (candidate && isValidLocationId(candidate)) {
      return candidate;
    }
  }
  return null;
};
